import dayjs from 'dayjs'
import { TTableHeader, TTableRowAction } from '@reapitform/common-ui'
import { AdminFormListItem, EnumCatalog } from '../../../../api/types'
import { getEnumLabel, getFormFamily } from '../../../../api/enums'

export const formsTableConfig = {
  tableDataCountMessage: { singular: 'form', plural: 'forms' },
  emptyMessage: 'No forms found. Create your first form to get started.',
}

/** Small pill shown in the Status column for a row whose version is production-locked — i.e. actually live in Prod. */
const LiveBadge = () => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: 600,
      color: 'var(--colour-text-success, #0a7d34)',
      backgroundColor: 'var(--colour-fill-success-lightest, #e3f6e8)',
    }}
  >
    Live
  </span>
)

/** Small pill shown in the Status column for a row whose version is not production-locked — i.e. still in draft. */
const DraftBadge = () => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: 600,
      color: 'var(--colour-text-secondary, #607890)',
      backgroundColor: 'var(--colour-fill-neutral-lightest, #f2f4f6)',
    }}
  >
    Draft
  </span>
)

/**
 * Row actions for the forms grid. Each row is one specific form version now, not one row per form, so
 * every action here operates on that version. In a read-only environment (TEST/PROD) "Edit" becomes
 * "Preview" — the builder itself renders read-only there (see FE-M5-3) — and "Delete"/"Copy" are hidden
 * entirely rather than shown and left to fail server-side, since ReadOnlyEnforcementMiddleware would
 * reject them anyway.
 *
 * A row whose version is production-locked (live in Prod — never editable again, anywhere, including dev)
 * gets the same "Edit" → "Preview" treatment and loses "Delete" (the backend rejects it too), but keeps
 * "Copy" — copying a locked version into a new, unlocked one is the intended escape hatch for changing
 * something that's gone live.
 *
 * The row menu just says "Delete" regardless of whether this ends up deleting only this version or the
 * whole form (a form can't be left with zero versions, so deleting its last version deletes the form) —
 * that distinction is spelled out in the confirmation dialog instead (see isWholeFormDelete in the table).
 */
export const getFormsRowActions = (
  navigate: (path: string) => void,
  onDeleteRequest: (form: AdminFormListItem) => void,
  onCopyRequest: (form: AdminFormListItem) => void,
  isReadOnly: boolean,
): TTableRowAction<AdminFormListItem>[] => [
  // Two mutually-exclusive rows rather than one dynamic label — actionTitle is a static ReactNode, not a
  // function of the row, so the label/visibility split has to happen here instead.
  {
    actionTitle: 'Edit',
    visibleFor: (form) => Boolean(form.versionReferenceID) && !isReadOnly && !form.isProductionLocked,
    onClick: (form) => {
      if (!form.versionReferenceID) return
      navigate(`/forms/${form.referenceID}/versions/${form.versionReferenceID}`)
    },
  },
  ...(isReadOnly
    ? []
    : [
        {
          actionTitle: 'Copy',
          visibleFor: (form: AdminFormListItem) => Boolean(form.versionReferenceID),
          onClick: (form: AdminFormListItem) => onCopyRequest(form),
        },
      ]),
  // Placed after Copy so it only ever surfaces as the last resort when nothing else is on offer —
  // read-only environments hide Copy above, so Preview is all that's left; a locked-but-writable
  // (dev/test) row still gets Copy ahead of it, per the same "escape hatch first" ordering.
  {
    actionTitle: 'Preview',
    visibleFor: (form) => Boolean(form.versionReferenceID) && (isReadOnly || form.isProductionLocked),
    onClick: (form) => {
      if (!form.versionReferenceID) return
      navigate(`/forms/${form.referenceID}/versions/${form.versionReferenceID}`)
    },
  },
  ...(isReadOnly
    ? []
    : [
        // Uses the design token for danger text to stay consistent with the rest of the admin app. The
        // row menu just says "Delete" — whether that ends up deleting the version or the whole form (see
        // isWholeFormDelete in the table) is spelled out in the confirmation dialog instead, not the menu.
        {
          actionTitle: <span style={{ color: 'var(--colour-text-danger, #d4285a)' }}>Delete</span>,
          visibleFor: (form: AdminFormListItem) => Boolean(form.versionReferenceID) && !form.isProductionLocked,
          onClick: (form: AdminFormListItem) => onDeleteRequest(form),
        },
      ]),
]

/** Column definitions for the forms grid; enum-typed columns are resolved to their display labels. */
export const getFormsTableHeaders = (enums: EnumCatalog | null): TTableHeader<AdminFormListItem>[] => [
  { key: 'name', headerTitle: 'Name', sortable: true, asNarrowRowTitle: true },
  {
    key: 'formType',
    headerTitle: 'Form Type',
    getColumnValue: (form) => getEnumLabel(enums, 'FormType', form.formType),
  },
  // `referenceID` isn't itself a "Family" value — it's just a real, otherwise-unused property borrowed to
  // satisfy TTableHeader's `key: keyof T` (there's no genuine `family` field on AdminFormListItem; the value
  // is derived from FormType via the enum catalog). getColumnValue is what actually renders here.
  {
    key: 'referenceID',
    headerTitle: 'Family',
    getColumnValue: (form) => getFormFamily(enums, form.formType),
  },
  {
    key: 'region',
    headerTitle: 'Region',
    getColumnValue: (form) => getEnumLabel(enums, 'Region', form.region),
  },
  {
    key: 'country',
    headerTitle: 'Country',
    getColumnValue: (form) => getEnumLabel(enums, 'Country', form.country),
  },
  { key: 'version', headerTitle: 'Version' },
  {
    key: 'isProductionLocked',
    headerTitle: 'Status',
    getColumnValue: (form) => {
      return form.isProductionLocked ? <LiveBadge /> : <DraftBadge />
    },
  },
  {
    key: 'validFromUTC',
    headerTitle: 'Valid From',
    getColumnValue: (form) => {
      return form.validFromUTC ? dayjs(form.validFromUTC).format('DD MMM YYYY') : ''
    },
  },
]
